import {ChangeDetectionStrategy, Component, Input} from '@angular/core';
import {Attachment} from '@ee/common/models';

@Component({
  selector: 'ee-intake-doc-metadata-info',
  template: `
    <span cdkOverlayOrigin #trigger="cdkOverlayOrigin" class="metadata-trigger">
      @if (useIconButton) {
        <!-- Rendered as a real icon button so it picks up the app's button chrome, for callers that sit
             it in a row of buttons. The anchor below stays the default for the document list. -->
        <button type="button" class="ee__button gray-bg" mat-icon-button
                [attr.aria-label]="tooltip" [tp]="tooltip"
                (click)="detailsOpen = !detailsOpen">
          <mat-icon fontSet="fa" [fontIcon]="fontIcon"></mat-icon>
        </button>
      } @else {
        <a class="cursor-pointer metadata-icon" [tp]="tooltip"
           (click)="detailsOpen = !detailsOpen"
           [class.open]="detailsOpen">
          <i [class]="iconClass"></i>
        </a>
      }
    </span>
    <ng-template
      cdkConnectedOverlay
      [cdkConnectedOverlayOrigin]="trigger"
      [cdkConnectedOverlayOpen]="detailsOpen"
      (overlayOutsideClick)="detailsOpen = false; $event.stopPropagation()">
      <div class="metadata-info ee__standard-shadow">
        @for (field of attachment.extracted_fields | keyvalue; track field.key) {
          <div class="mb-2">
            @if (field.key === 'tenant_names') {
              <div class="metadata-section-header">Tenants</div>
              <div>
                <i class="fa-regular fa-users"></i>
                {{ field.value }}
              </div>
            } @else if (field.key === 'lease_end_date') {
              <div class="metadata-section-header">Lease End Date</div>
              <div>
                <i class="fa-regular fa-calendar-xmark"></i>
                {{ field.value }}
              </div>
            } @else if (field.key === 'lease_start_date') {
              <div class="metadata-section-header">Lease Start Date</div>
              <div>
                <i class="fa-regular fa-calendar-check"></i>
                {{ field.value }}
              </div>
            } @else if (field.key === 'owner_names') {
              <div class="metadata-section-header">Property Owner Names</div>
              <div>
                <i class="fa-regular fa-users"></i>
                {{ field.value }}
              </div>
            } @else if (field.key === 'lease_property_address') {
              <div class="metadata-section-header">Property Address</div>
              <div>
                <i class="fa-regular fa-users"></i>
                {{ field.value }}
              </div>
            } @else {
              <div class="metadata-section-header">{{field.key}}</div>
              <div>
                <i class="fa-regular code"></i>
                {{ field.value }}
              </div>
            }
          </div>
        }
      </div>
    </ng-template>
  `,
  styles: `
    .metadata-trigger {
      display: inline-flex;
      align-items: center;
    }

    .metadata-icon {
      font-size: 12px;
      background-color: var(--color-surface, #fff);
      height: 20px;
      border-radius: 10px;
      line-height: 19px;
      width: 20px;
      cursor: pointer;
      color: var(--color-on-surface-variant, #6f6f6f);

      &:hover {
        background-color: var(--color-surface-container-high, #d7d7d7);
      }
    }

    .metadata-info {
      background-color: var(--color-surface, #fff);
      border-radius: 8px;
      padding: 1rem;
      color: var(--color-on-surface, #424242);
      font-size: 1rem;
      font-weight: 500;

      .metadata-section-header {
        font-size: 12px;
        font-weight: 600;
        color: var(--color-on-surface-variant, #6F6F6F);
        text-transform: uppercase;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class IntakeDocMetadataInfoComponent {
  public detailsOpen = false;
  @Input({required: true}) attachment: Attachment;
  /** Overridable so a caller can match its own icon set; the default keeps the document list unchanged. */
  @Input() iconClass = 'far fa-code';
  @Input() tooltip = 'Metadata';
  /** Render the trigger as an app icon button rather than the document list's bare anchor. */
  @Input() useIconButton = false;
  /** FontAwesome icon name used when `useIconButton` is set. */
  @Input() fontIcon = 'fa-wand-magic-sparkles';
}

